//third-party
import { multiply    } from "dinero.js";
import { LocalDate  } from "@js-joda/core";
import { Card, Placeholder } from "react-bootstrap";

//spotsync
import { Money, showMoney, toCents } from "../Data/Money";
import { showDate } from "../Data/Date";
import { ListedPlan, SubscribePreviewResp } from "../Data/ApiTypes";
import { AddSubtract } from "./AddSubtract";
import { MonthlyPriceBreakdown } from "./MonthlyPriceBreakdown";
import { useState, useEffect } from "react";
import { Api } from "../Data/Api";
import { getOrdinal } from "../Data/Subscription";
import { isPreviewProrated } from "../Data/Common";


interface MonthlyParkingDetailsProps {
    color?:           string;
    start:            LocalDate;
    subscriptionId:   number;
    selOpt:           ListedPlan;
    disabled?:        boolean;
    originalQuantity: number;
    quantity:         number;
    proratedFee:      Money;
    setQuantity?:     ( qty: number ) => void;
    isAch:            boolean;
}

export function MonthlyParkingModify( props: MonthlyParkingDetailsProps ) {
    const {
            start,
            subscriptionId,
            originalQuantity: prevQty,
            quantity:         qty,
            selOpt,
            proratedFee,
            setQuantity,
            disabled = false,
            isAch } = { ...props };

    const [isLoading, setIsLoading] = useState(true);
    const [previewData, setPreviewData] = useState<SubscribePreviewResp | null>(null);
    const [newPrice, setNewPrice] = useState<SubscribePreviewResp | null>(null);
    const spotsAdded = Math.max(0, qty - prevQty);

    useEffect(() => {
        const fetchPreview = async () => {
            setIsLoading(true);
            const req = {
                start,
                quantity: spotsAdded
            };
            const result = await Api.modifySubscriptionPreview(subscriptionId, req);
            
            if (result.isOk) {
                setPreviewData(result.value);
            }

            const newPrice = await Api.modifySubscriptionPreview(subscriptionId, {
                start,
                quantity: qty
            });
            
            if (newPrice.isOk) {
                setNewPrice(newPrice.value);
            }

            setIsLoading(false);
        };

        fetchPreview();
    }, [qty]);

    const rate = multiply(selOpt.price, qty ?? 1);

    return <Card className={`border-2 border-primary my-1`}>
        <Card.Header className="header-gradient text-center">
            Update Plan Parking
        </Card.Header>
        <Card.Body>
            <div className="mt-2 w-auto gap-3" style={{ display: "grid", gridTemplateColumns: "auto max-content" }}>
                <div>
                    Plan Name
                </div>
                <div className="text-secondary">
                    {selOpt.description}
                </div>
                <div className="text-start d-flex align-items-center">
                    Current # Spots
                </div>
                <div className="justify-content-end d-inline-flex align-items-center justify-content-start justify-content-md-center gap-1">
                    <AddSubtract variant="secondary" quantity={prevQty} disabled={true} set={ () => 0 } />
                </div>

                {( qty !== undefined && setQuantity !== undefined ) && <>
                    <div className="text-start d-flex align-items-center">
                        New # Spots
                    </div>
                    <div className="justify-content-end d-inline-flex align-items-center justify-content-start justify-content-md-center gap-1">
                        <AddSubtract quantity={qty} set={ qty => disabled !== true && setQuantity( qty ) } />
                    </div>
                </>}

                <div className="text-start">
                    Spots Added
                </div>
                <div className="text-end">
                    {spotsAdded}
                </div>

                <div className="text-start">Start Date</div>
                <div className="text-end">{showDate( start )}</div>

                <div style={{ gridColumn: "1 / 3", width: "100%" }}>
                    {isLoading ? (
                        <div className="border rounded p-2">
                            <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: "0.5rem" }}>
                                <Placeholder as="div" animation="wave" className="text-start"><Placeholder xs={6} /></Placeholder><Placeholder as="div" animation="wave" className="text-end"><Placeholder xs={4} /></Placeholder>
                                <Placeholder as="div" animation="wave" className="text-start"><Placeholder xs={4} /></Placeholder><Placeholder as="div" animation="wave" className="text-end"><Placeholder xs={3} /></Placeholder>
                                <Placeholder as="div" animation="wave" className="text-start"><Placeholder xs={5} /></Placeholder><Placeholder as="div" animation="wave" className="text-end"><Placeholder xs={3} /></Placeholder>
                            </div>
                        </div>
                    ) : previewData && toCents(previewData.price) > 0 && (
                        <div className="fade-in">
                            <MonthlyPriceBreakdown 
                                price={previewData.price}
                                fees={previewData.fees}
                                showTotalLabel="Price"
                                showProratedHint={isPreviewProrated(previewData)}
                            />
                        </div>
                    )}
                </div>  
            </div>
            <div className="mt-3">
                 {newPrice && (
                    <div className="text-start">Monthly Payment: <span className="fw-bold">{newPrice ? showMoney(multiply(newPrice.fullUnitPrice, newPrice.quantity)) : '--'}</span>*<br />
                        <span className="text-secondary" style={{fontSize:".8rem"}}>This is the amount that will be charged for renewals.</span><br />
                        <span className="text-secondary" style={{fontSize:".7rem"}}>*Price does not include applicable fees or add-on services.</span>
                    </div>
                )}
            </div>
        </Card.Body>
    </Card>;
}